import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import ScheduleIcon from "@mui/icons-material/Schedule";
import moment from "moment";
import { useSnackbar } from "notistack";
import { useContentSetting } from "src/Hooks/ContentContext/ContentSettingState";
import {
  getMemberTrackerDetailApi,
  leaveMemberTrackerApi,
} from "src/DAL/tracker/tracker";
import {
  getJourneySteps,
  getSubmissionAnswerRows,
  getTrackerListMeta,
  isLeftTracker,
  LEAVE_REASONS,
  PRIMARY,
} from "./memberTrackersStorage";
import JourneyPasswordGate, {
  isJourneyPasswordRequired,
} from "./JourneyPasswordGate";

const BORDER = "#e2e8f0";
const MUTED = "#64748b";
const INK = "#0f172a";
const LEAVE_NOTE_MAX = 500;
const LEFT_STATUS_COLOR = "#64748b";

/** Confirm leaving a journey — reason required, note optional. */
const LeaveJourneyDialog = ({ open, submitting, error, onClose, onConfirm }) => {
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    if (open) {
      setReason("");
      setNote("");
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle sx={{ fontWeight: 800, color: INK }}>
        Leave this journey?
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 14, color: MUTED, lineHeight: 1.5, mb: 2 }}>
          Your coach will be notified. Your history stays saved and you can
          rejoin later.
        </Typography>
        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: INK, mb: 0.5 }}>
          Why are you leaving?
        </Typography>
        <RadioGroup
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          sx={{ mb: 1.5 }}
        >
          {LEAVE_REASONS.map((item) => (
            <FormControlLabel
              key={item.value}
              value={item.value}
              control={<Radio size="small" disabled={submitting} />}
              label={item.label}
            />
          ))}
        </RadioGroup>
        <TextField
          fullWidth
          multiline
          minRows={3}
          size="small"
          label="Anything you'd like to add? (optional)"
          value={note}
          disabled={submitting}
          onChange={(e) => setNote(e.target.value.slice(0, LEAVE_NOTE_MAX))}
          inputProps={{ maxLength: LEAVE_NOTE_MAX }}
          helperText={`${note.length}/${LEAVE_NOTE_MAX}`}
        />
        {error ? (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            {error}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting} sx={{ color: MUTED }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          disabled={!reason || submitting}
          onClick={() => onConfirm({ reason, note: note.trim() })}
          startIcon={
            submitting ? <CircularProgress size={14} color="inherit" /> : null
          }
        >
          Leave journey
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default function MemberTrackerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { userInfo } = useContentSetting();
  const [selectedId, setSelectedId] = useState(null);
  const [tracker, setTracker] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loadState, setLoadState] = useState("loading");
  const [loadNotFound, setLoadNotFound] = useState(false);
  const [submitNotice, setSubmitNotice] = useState("");
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [leaveError, setLeaveError] = useState("");
  const [lockedTracker, setLockedTracker] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const { enqueueSnackbar } = useSnackbar();

  const openLeaveDialog = () => {
    setLeaveError("");
    setLeaveOpen(true);
  };

  const handleLeave = async ({ reason, note }) => {
    setLeaving(true);
    setLeaveError("");
    try {
      const res = await leaveMemberTrackerApi(id, { reason, note });
      if (res?.code !== 200) {
        setLeaveError(res?.message || "Could not leave this journey");
        return;
      }
      const next = res.tracker || {};
      // Keep forms/submissions from detail; apply only the leave fields.
      setTracker((prev) => ({
        ...prev,
        membership: "left",
        leftAt: next.leftAt || new Date().toISOString(),
        leftReason: next.leftReason || reason,
        statusName: next.statusName || "Left",
        statusColor: next.statusColor || LEFT_STATUS_COLOR,
      }));
      setSubmitNotice("");
      setLeaveOpen(false);
      enqueueSnackbar(res.message || "You have left this journey", {
        variant: "success",
      });
    } catch (_err) {
      setLeaveError("Could not leave this journey");
    } finally {
      setLeaving(false);
    }
  };

  useEffect(() => {
    if (location.state?.submitted) {
      setSubmitNotice("Submitted — your coach can see your answers.");
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadState("loading");
      try {
        const res = await getMemberTrackerDetailApi(id);
        if (cancelled) return;
        if (res?.code === 200 && res.tracker) {
          setTracker({
            ...res.tracker,
            _memberSubmissions: res.submissions || [],
          });
          setSubmissions(
            (res.submissions || []).map((row) => ({
              ...row,
              id: String(row._id || row.id),
            }))
          );
          setLoadState("ready");
        } else if (isJourneyPasswordRequired(res)) {
          setTracker(null);
          setSubmissions([]);
          setLockedTracker(res.tracker || {});
          setLoadState("locked");
        } else {
          setTracker(null);
          setSubmissions([]);
          setLoadNotFound(res?.code === 404);
          setLoadState("error");
        }
      } catch (_err) {
        if (cancelled) return;
        setTracker(null);
        setSubmissions([]);
        setLoadNotFound(false);
        setLoadState("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, userInfo?._id, reloadKey]);

  const steps = useMemo(
    () => (tracker ? getJourneySteps(tracker, submissions) : []),
    [tracker, submissions]
  );
  const meta = useMemo(
    () => (tracker ? getTrackerListMeta(tracker) : null),
    [tracker]
  );

  useEffect(() => {
    if (!submissions.length) {
      setSelectedId(null);
      return;
    }
    setSelectedId((prev) =>
      prev && submissions.some((s) => s.id === prev) ? prev : submissions[0].id
    );
  }, [submissions]);

  const selected = useMemo(
    () => submissions.find((s) => s.id === selectedId) || null,
    [submissions, selectedId]
  );
  const answers = useMemo(() => {
    const rows = getSubmissionAnswerRows(selected);
    // Personal info lives on the member profile — hide from answer list.
    return rows.filter((row) => {
      const label = String(row.label || "").toLowerCase();
      if (label.includes("first name")) return false;
      if (label.includes("last name")) return false;
      if (label === "email" || label.includes("email address")) return false;
      if (label.includes("phone")) return false;
      return true;
    });
  }, [selected]);

  if (loadState === "loading") {
    return (
      <div className="container-fluid">
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress size={32} />
        </Box>
      </div>
    );
  }

  if (loadState === "locked") {
    return (
      <JourneyPasswordGate
        idOrSlug={id}
        tracker={lockedTracker}
        onUnlocked={() => {
          setLockedTracker(null);
          setReloadKey((key) => key + 1);
        }}
      />
    );
  }

  if (!tracker) {
    return (
      <div className="container-fluid">
        <button
          type="button"
          className="small-contained-button mb-3"
          onClick={() => navigate("/trackers")}
        >
          Back to Journeys
        </button>
        <Typography sx={{ fontWeight: 700, color: INK }}>
          {loadNotFound ? "Journey not found" : "Couldn't load this journey"}
        </Typography>
        <Typography sx={{ color: MUTED, mt: 0.5 }}>
          {loadNotFound
            ? "This journey may have been removed or you no longer have access."
            : "Something went wrong while loading it. Please try again."}
        </Typography>
        {!loadNotFound ? (
          <button
            type="button"
            className="small-contained-outlined-button mt-3"
            onClick={() => setReloadKey((key) => key + 1)}
          >
            Try again
          </button>
        ) : null}
      </div>
    );
  }

  const isLeft = isLeftTracker(tracker);
  // Enrolled members (active or finished) can leave; already-left / not started cannot.
  const canLeave =
    !isLeft &&
    (tracker.membership === "active" || tracker.membership === "completed");
  const canContinue = !isLeft && Boolean(meta?.canSubmit);
  const waitingForDate =
    !isLeft && Boolean(meta?.lockedStep && !meta?.canSubmit);

  return (
    <div className="container-fluid">
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 2.5,
          cursor: "pointer",
          width: "fit-content",
          color: MUTED,
          "&:hover": { color: PRIMARY },
        }}
        onClick={() => navigate("/trackers")}
      >
        <ArrowBackIcon sx={{ fontSize: 18 }} />
        <Typography sx={{ fontSize: 13.5, fontWeight: 650 }}>
          All journeys
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          mb: 3,
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1 }}>
            <h2 style={{ margin: 0 }}>{tracker.name}</h2>
            <Chip
              size="small"
              label={tracker.statusName || "Active"}
              sx={{
                height: 24,
                fontWeight: 700,
                fontSize: 11.5,
                bgcolor: `${tracker.statusColor || PRIMARY}18`,
                color: tracker.statusColor || PRIMARY,
              }}
            />
          </Box>
          <Typography
            sx={{ color: MUTED, fontSize: 14.5, lineHeight: 1.55, maxWidth: 640 }}
          >
            {tracker.description}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 1.5 }}>
            <Typography sx={{ fontSize: 13, color: MUTED }}>
              Coach:{" "}
              <Box component="span" sx={{ color: INK, fontWeight: 700 }}>
                {tracker.coachName || "—"}
              </Box>
            </Typography>
            {tracker.startedAt ? (
              <Typography sx={{ fontSize: 13, color: MUTED }}>
                Started:{" "}
                <Box component="span" sx={{ color: INK, fontWeight: 650 }}>
                  {moment(tracker.startedAt).format("D MMM YYYY")}
                </Box>
              </Typography>
            ) : null}
            {(meta?.nextReviewAt || tracker.nextReviewAt) &&
            !canContinue &&
            !isLeft ? (
              <Typography sx={{ fontSize: 13, color: MUTED }}>
                Next review:{" "}
                <Box component="span" sx={{ color: INK, fontWeight: 650 }}>
                  {moment(meta?.nextReviewAt || tracker.nextReviewAt).format(
                    "D MMM YYYY"
                  )}
                </Box>
              </Typography>
            ) : null}
          </Box>
          {submitNotice ? (
            <Typography
              sx={{
                mt: 1.5,
                fontSize: 13.5,
                fontWeight: 650,
                color: PRIMARY,
              }}
            >
              {submitNotice}
            </Typography>
          ) : null}
          {isLeft ? (
            <Alert severity="info" sx={{ mt: 1.5, maxWidth: 640 }}>
              {tracker.leftAt
                ? `You left this journey on ${moment(tracker.leftAt).format(
                    "D MMM YYYY"
                  )}.`
                : "You left this journey."}
            </Alert>
          ) : null}
        </Box>

        {isLeft ? (
          <button
            type="button"
            className="small-contained-button"
            onClick={() => navigate(`/trackers/${id}/submit`)}
          >
            Rejoin journey
          </button>
        ) : canContinue || waitingForDate || canLeave ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: { xs: "flex-start", sm: "flex-end" },
              gap: 0.75,
            }}
          >
            {canContinue ? (
              <button
                type="button"
                className="small-contained-button"
                onClick={() => {
                  setSubmitNotice("");
                  navigate(`/trackers/${id}/submit`);
                }}
              >
                {submissions.length ? "Continue check-in" : "Start check-in"}
                {meta?.currentStep?.name ? `: ${meta.currentStep.name}` : ""}
              </button>
            ) : waitingForDate ? (
              <Box sx={{ textAlign: { xs: "left", sm: "right" }, maxWidth: 280 }}>
                <button
                  type="button"
                  className="small-contained-button"
                  disabled
                  style={{ opacity: 0.45, cursor: "not-allowed" }}
                >
                  {meta.lockedStep?.name || "Next check-in"}
                </button>
                <Typography sx={{ mt: 0.75, fontSize: 12.5, color: MUTED, lineHeight: 1.45 }}>
                  Opens on{" "}
                  {meta.lockedStep?.unlockAt
                    ? moment(meta.lockedStep.unlockAt).format("D MMM YYYY")
                    : "the scheduled date"}
                </Typography>
              </Box>
            ) : null}
            {canLeave ? (
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={openLeaveDialog}
                sx={{
                  fontSize: 12.5,
                  fontWeight: 700,
                  textTransform: "none",
                  borderRadius: "8px",
                }}
              >
                Leave journey
              </Button>
            ) : null}
          </Box>
        ) : canLeave ? (
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={openLeaveDialog}
            sx={{
              fontSize: 12.5,
              fontWeight: 700,
              textTransform: "none",
              borderRadius: "8px",
            }}
          >
            Leave journey
          </Button>
        ) : null}
      </Box>

      <LeaveJourneyDialog
        open={leaveOpen}
        submitting={leaving}
        error={leaveError}
        onClose={() => setLeaveOpen(false)}
        onConfirm={handleLeave}
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1.05fr 0.95fr" },
          gap: 2.5,
          alignItems: "start",
        }}
      >
        {/* Left: journey + submissions */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          <Box
            sx={{
              border: `1px solid ${BORDER}`,
              borderRadius: "14px",
              bgcolor: "#fff",
              p: { xs: 2, md: 2.5 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1.5,
              }}
            >
              <Typography sx={{ fontWeight: 800, fontSize: 16, color: INK }}>
                Your journey
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: PRIMARY }}>
                {meta?.progress || 0}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={meta?.progress || 0}
              sx={{
                height: 8,
                borderRadius: 99,
                bgcolor: "#eef2f6",
                mb: 2.5,
                "& .MuiLinearProgress-bar": {
                  borderRadius: 99,
                  bgcolor: PRIMARY,
                },
              }}
            />

            <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
              {steps.map((step, i) => {
                const done = step.state === "done";
                const current = step.state === "current";
                const locked = step.state === "locked";
                const isSelected =
                  step.submission && step.submission.id === selectedId;
                return (
                  <Box
                    key={step.id}
                    sx={{
                      display: "flex",
                      gap: 1.75,
                      position: "relative",
                      pb: i < steps.length - 1 ? 2.5 : 0,
                    }}
                  >
                    {i < steps.length - 1 ? (
                      <Box
                        sx={{
                          position: "absolute",
                          left: 13,
                          top: 28,
                          bottom: 0,
                          width: 2,
                          bgcolor: done ? PRIMARY : "#e2e8f0",
                        }}
                      />
                    ) : null}
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        flexShrink: 0,
                        display: "grid",
                        placeItems: "center",
                        zIndex: 1,
                        bgcolor: done || current ? PRIMARY : "#fff",
                        color: done || current ? "#fff" : MUTED,
                        border: `2px solid ${
                          done || current
                            ? PRIMARY
                            : locked
                              ? "#f59e0b"
                              : "#cbd5e1"
                        }`,
                      }}
                    >
                      {done ? (
                        <CheckCircleIcon sx={{ fontSize: 16 }} />
                      ) : current || locked ? (
                        <ScheduleIcon sx={{ fontSize: 15 }} />
                      ) : (
                        <RadioButtonUncheckedIcon sx={{ fontSize: 15 }} />
                      )}
                    </Box>
                    <Box sx={{ minWidth: 0, pt: 0.15, flex: 1 }}>
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "center",
                          gap: 1,
                          mb: 0.35,
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: current || isSelected || locked ? 800 : 700,
                            fontSize: 14.5,
                            color: INK,
                          }}
                        >
                          {step.name}
                        </Typography>
                        {step.dueLabel ? (
                          <Chip
                            size="small"
                            label={step.dueLabel}
                            sx={{
                              height: 22,
                              fontSize: 11,
                              fontWeight: 650,
                              bgcolor: "#f1f5f9",
                              color: MUTED,
                            }}
                          />
                        ) : null}
                        {current ? (
                          <Chip
                            size="small"
                            label="Up next"
                            sx={{
                              height: 22,
                              fontSize: 11,
                              fontWeight: 700,
                              bgcolor:
                                "color-mix(in srgb, var(--portal-theme-primary) 14%, white)",
                              color: PRIMARY,
                            }}
                          />
                        ) : null}
                        {locked ? (
                          <Chip
                            size="small"
                            label={
                              step.unlockAt
                                ? `Opens ${moment(step.unlockAt).format("D MMM")}`
                                : "Locked"
                            }
                            sx={{
                              height: 22,
                              fontSize: 11,
                              fontWeight: 700,
                              bgcolor: "#fff7ed",
                              color: "#c2410c",
                            }}
                          />
                        ) : null}
                      </Box>
                      {step.submission ? (
                        <Typography
                          component="button"
                          type="button"
                          onClick={() => setSelectedId(step.submission.id)}
                          sx={{
                            border: "none",
                            background: "none",
                            p: 0,
                            cursor: "pointer",
                            fontSize: 12.5,
                            fontWeight: isSelected ? 800 : 700,
                            color: isSelected ? PRIMARY : MUTED,
                            textAlign: "left",
                            "&:hover": { color: PRIMARY },
                          }}
                        >
                          Submitted{" "}
                          {moment(step.submission.submittedAt).format(
                            "D MMM YYYY"
                          )}
                          {isSelected ? " · showing answers" : " · show answers"}
                        </Typography>
                      ) : current ? (
                        <Typography sx={{ fontSize: 12.5, color: MUTED }}>
                          Ready when you are — open Start / Continue to answer
                          on a new screen.
                        </Typography>
                      ) : locked ? (
                        <Typography sx={{ fontSize: 12.5, color: MUTED }}>
                          Available from{" "}
                          {step.unlockAt
                            ? moment(step.unlockAt).format("D MMM YYYY")
                            : "the scheduled date"}
                          . The continue button unlocks then.
                        </Typography>
                      ) : (
                        <Typography sx={{ fontSize: 12.5, color: MUTED }}>
                          Unlocks later in your journey.
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* <Box
            sx={{
              border: `1px solid ${BORDER}`,
              borderRadius: "14px",
              bgcolor: "#fff",
              p: { xs: 2, md: 2.5 },
            }}
          >
            <Typography sx={{ fontWeight: 800, fontSize: 16, color: INK, mb: 1.75 }}>
              Your submissions
            </Typography>
            {submissions.length === 0 ? (
              <Typography sx={{ color: MUTED, fontSize: 14 }}>
                No submissions yet on this journey.
              </Typography>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {submissions.map((row) => {
                  const active = row.id === selectedId;
                  return (
                    <Box
                      key={row.id}
                      component="button"
                      type="button"
                      onClick={() => setSelectedId(row.id)}
                      sx={{
                        border: `1.5px solid ${active ? PRIMARY : BORDER}`,
                        borderRadius: "12px",
                        p: 1.5,
                        bgcolor: active
                          ? "color-mix(in srgb, var(--portal-theme-primary) 6%, white)"
                          : "#fafbfc",
                        textAlign: "left",
                        cursor: "pointer",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 1,
                          mb: 0.4,
                        }}
                      >
                        <Typography
                          sx={{ fontWeight: 750, fontSize: 13.5, color: INK }}
                        >
                          {row.formName || "Submission"}
                        </Typography>
                        <Chip
                          size="small"
                          label={row.statusName || "Submitted"}
                          sx={{
                            height: 20,
                            fontSize: 10.5,
                            fontWeight: 700,
                            bgcolor: `${row.statusColor || PRIMARY}18`,
                            color: row.statusColor || PRIMARY,
                          }}
                        />
                      </Box>
                      <Typography sx={{ fontSize: 12, color: MUTED }}>
                        {moment(row.submittedAt).format("D MMM YYYY · HH:mm")}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box> */}
        </Box>

        {/* Right: answers for selected submission */}
        <Box
          sx={{
            border: `1px solid ${BORDER}`,
            borderRadius: "14px",
            bgcolor: "#fff",
            overflow: "hidden",
            position: { lg: "sticky" },
            top: { lg: 16 },
          }}
        >
          {!selected ? (
            <Box sx={{ p: 2.5 }}>
              <Typography sx={{ fontWeight: 800, fontSize: 16, color: INK, mb: 0.75 }}>
                Your answers
              </Typography>
              <Typography sx={{ color: MUTED, fontSize: 14 }}>
                Select a submission on the left to see your answers here.
              </Typography>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  px: 2.25,
                  py: 1.75,
                  borderBottom: `1px solid ${BORDER}`,
                  bgcolor: "#fafbfc",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 1,
                    mb: 0.5,
                  }}
                >
                  <Typography sx={{ fontWeight: 800, fontSize: 16, color: INK }}>
                    {selected.formName || "Your answers"}
                  </Typography>
                  <Chip
                    size="small"
                    label={selected.statusName || "Submitted"}
                    sx={{
                      height: 22,
                      fontSize: 11,
                      fontWeight: 700,
                      bgcolor: `${selected.statusColor || PRIMARY}18`,
                      color: selected.statusColor || PRIMARY,
                    }}
                  />
                </Box>
                <Typography sx={{ fontSize: 12.5, color: MUTED }}>
                  {moment(selected.submittedAt).format(
                    "ddd, D MMM YYYY · HH:mm"
                  )}
                  {selected.coachName ? ` · Coach ${selected.coachName}` : ""}
                </Typography>
              </Box>

              {answers.length === 0 ? (
                <Box sx={{ p: 2.5 }}>
                  <Typography sx={{ color: MUTED, fontSize: 14 }}>
                    No answers were stored for this submission.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ maxHeight: { lg: "70vh" }, overflowY: "auto" }}>
                  {answers.map((row, index) => (
                    <Box
                      key={`${row.label}_${index}`}
                      sx={{
                        px: 2.25,
                        py: 1.6,
                        borderBottom:
                          index < answers.length - 1
                            ? `1px solid ${BORDER}`
                            : "none",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 11.5,
                          fontWeight: 700,
                          color: MUTED,
                          mb: 0.4,
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                        }}
                      >
                        {row.label}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 14.5,
                          fontWeight: 650,
                          color: INK,
                          lineHeight: 1.5,
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {row.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

              {selected.coachNotes ? (
                <Box
                  sx={{
                    px: 2.25,
                    py: 1.75,
                    borderTop: `1px solid ${BORDER}`,
                    bgcolor:
                      "color-mix(in srgb, var(--portal-theme-primary) 6%, white)",
                  }}
                >
                  <Typography
                    sx={{ fontWeight: 800, fontSize: 13, color: INK, mb: 0.5 }}
                  >
                    Coach note
                  </Typography>
                  <Typography sx={{ fontSize: 13.5, color: INK, lineHeight: 1.5 }}>
                    {selected.coachNotes}
                  </Typography>
                </Box>
              ) : null}
            </>
          )}
        </Box>
      </Box>
    </div>
  );
}
